import { FilePreview } from "@components/Views/FilePreview";
import React, { useState } from "react";
import { UniversalInputProps } from "@interfaces/System/UniversalInputProps";
import { useAsync } from "@hooks";
import { fileService } from "@services";
import { Button, FormHelperText, Grid, Link, Tooltip, Typography } from "@material-ui/core";
import { Visibility } from "@material-ui/icons";
import { getFileUrl } from "@modules/Utils";
import { Skeleton } from "@material-ui/lab";
import FilePickerDialog from "@components/Controls/FilePicker/FilePickerDialog";

export type UFileAutocompleteExtraProps = Partial<{
    /** id файлов, уже занятых другими строками этого же множественного
     * атрибута — диалог выбора не даёт выбрать их повторно */
    excludeFileIds: number[];
    /** Куда открыть диалог, если у ЭТОЙ строки ещё нет своего значения (новая
     * пустая строка, добавленная через "Новое значение") — id другого фото
     * того же атрибута как якорь, либо поиск папки по названию модели, см.
     * resolveFilePickerAnchor(). Если у строки уже есть value — используется
     * оно (текущий файл), эти пропы не нужны. */
    anchorFileId: number | null;
    initialSearchTerm: string;
}>;

const UFileAutocomplete = ({
    excludeFileIds,
    anchorFileId,
    initialSearchTerm,
    ...props
}: UFileAutocompleteExtraProps & UniversalInputProps) => {
    const { name, value, onChange, label, disabled, error, helperText } = props;
    const [pickerOpen, setPickerOpen] = useState(false);

    const { data: file, loadingStatus } = useAsync(fileService.getOneFile, {
        functionArgs: [value],
        setLoadingStatusOnUpdate: false,
        canLoad: !!value,
    });

    const handleSelect = (fileId: number) => {
        onChange(name, fileId);
    };

    if (loadingStatus === "loading") return <Skeleton width="100%" height={50} />;

    return (
        <>
            <Grid container alignItems="center" spacing={1}>
                <Grid item xs>
                    <Typography variant="body2" color={value ? "textPrimary" : "textSecondary"}>
                        {value ? file?.name || "…" : "Файл не выбран"}
                    </Typography>
                    {error && <FormHelperText error>{helperText}</FormHelperText>}
                </Grid>
                <Grid item>
                    <Button
                        color="primary"
                        variant="outlined"
                        size="small"
                        disabled={disabled}
                        onClick={() => setPickerOpen(true)}
                    >
                        {label || "Выбрать файл"}
                    </Button>
                </Grid>
                <Grid item>
                    <Tooltip title="Посмотреть файл" arrow>
                        <span>
                            <Button
                                component={Link}
                                color="primary"
                                disabled={loadingStatus !== "loaded" || !!!value}
                                target="_blank"
                                href={getFileUrl(file?.path!)}
                            >
                                <Visibility />
                            </Button>
                        </span>
                    </Tooltip>
                </Grid>
            </Grid>

            {loadingStatus === "loaded" && value && (
                <FilePreview
                    file={file?.path ?? ""}
                    containerStyle={{
                        maxWidth: 200,
                        maxHeight: 200,
                    }}
                    imageStyle={{
                        top: "50%",
                        bottom: "50%",
                    }}
                />
            )}

            <FilePickerDialog
                open={pickerOpen}
                value={value ? +value : anchorFileId ?? null}
                initialSearchTerm={value ? undefined : initialSearchTerm}
                onSelect={handleSelect}
                onClose={() => setPickerOpen(false)}
                excludeFileIds={excludeFileIds}
            />
        </>
    );
};

export default UFileAutocomplete;
